import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Rocket, Settings, Users, Search, PlayCircle } from "lucide-react";
import DemoScheduler from "@/components/DemoScheduler";

const timelineSteps = [
    {
        week: "Semana 1",
        title: "Diagnóstico & Setup",
        description: "Mapeamento dos processos atuais, definição de KPIs e configuração inicial do ambiente AROAN.",
        icon: Search,
        details: [
            "Workshop de mapeamento de processos",
            "Definição de canais de entrada",
            "Setup do ambiente na nuvem",
            "Configuração de acessos e segurança"
        ]
    },
    {
        week: "Semanas 2-3",
        title: "Configuração No-Code",
        description: "Construção dos fluxos de trabalho, formulários inteligentes e integração com ferramentas legadas.",
        icon: Settings,
        details: [
            "Criação de fluxos no Workflow Builder",
            "Personalização de formulários dinâmicos",
            "Integração com ERP/CRM via API",
            "Configuração de regras de negócio"
        ]
    },
    {
        week: "Semanas 4-5",
        title: "Treinamento da VIA & Piloto",
        description: "Alimentação da IA com base de conhecimento e execução de piloto controlado com equipe selecionada.",
        icon: Users,
        details: [
            "Ingestão de documentos na VIA (IA)",
            "Treinamento de contexto específico",
            "Homologação com key-users",
            "Ajustes finos de automação"
        ]
    },
    {
        week: "Semana 6",
        title: "Go-Live & Acompanhamento",
        description: "Virada oficial da operação, monitoramento intensivo e entrega do playbook de operação.",
        icon: Rocket,
        details: [
            "Comunicação oficial de lançamento",
            "Monitoramento em tempo real",
            "Suporte assistido (Hypercare)",
            "Entrega de dashboards de ROI"
        ]
    }
];

const HowItWorksPage = () => {
    const [demoOpen, setDemoOpen] = useState(false);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="Como Funciona: Implementação da Aroan em 6 Semanas"
                description="Veja o passo a passo para colocar a Aroan em produção em até 6 semanas: diagnóstico, configuração no-code dos fluxos de chamados, treinamento da VIA e go-live."
                canonical="https://aroan.com.br/como-funciona"
                ogType="website"
                article={false}
            />
            <BreadcrumbSchema
                items={[
                    { name: "Home", url: "https://aroan.com.br/" },
                    { name: "Como Funciona", url: "https://aroan.com.br/como-funciona" },
                ]}
            />
            <Header />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gradient-hero text-center">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in">
                        Implementação Ágil em <span className="text-primary">6 Semanas</span>
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 animate-fade-in-delay-1">
                        Esqueça implementações de meses ou anos. Nossa metodologia No-Code + AI-Native permite transformar sua operação em tempo recorde.
                    </p>
                    <Button size="lg" className="group animate-fade-in-delay-2" onClick={() => setDemoOpen(true)}>
                        Comece Sua Jornada Agora
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>
            </section>

            {/* Timeline Section */}
            <section className="py-20 bg-background relative overflow-hidden">
                {/* Connecting Line (Desktop) */}
                <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2" />

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="space-y-12 lg:space-y-24">
                        {timelineSteps.map((step, index) => {
                            const Icon = step.icon;
                            const isEven = index % 2 === 0;

                            return (
                                <div key={index} className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-16 ${isEven ? 'lg:flex-row-reverse' : ''}`}>

                                    {/* Content Side */}
                                    <div className="flex-1 w-full lg:text-right">
                                        <div className={`bg-card p-8 rounded-2xl border border-border shadow-sm hover:shadow-md transition-shadow ${isEven ? 'lg:text-left' : 'lg:text-right'}`}>
                                            <div className={`flex items-center gap-4 mb-4 ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                                                    <Icon className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <span className="text-sm font-bold text-primary uppercase tracking-wider">{step.week}</span>
                                                    <h3 className="text-2xl font-bold">{step.title}</h3>
                                                </div>
                                            </div>
                                            <p className="text-muted-foreground mb-6 leading-relaxed">
                                                {step.description}
                                            </p>
                                            <ul className={`space-y-2 ${isEven ? 'items-start' : 'items-end lg:items-end'}`}>
                                                {step.details.map((detail, idx) => (
                                                    <li key={idx} className={`flex items-center gap-2 text-sm text-foreground/80 ${isEven ? 'justify-start' : 'justify-start lg:justify-end'}`}>
                                                        {isEven && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />}
                                                        <span>{detail}</span>
                                                        {!isEven && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 lg:order-last" />}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Center Point (Desktop) */}
                                    <div className="hidden lg:flex items-center justify-center w-12 h-12 rounded-full bg-background border-4 border-primary z-10 shadow-lg shrink-0">
                                        <span className="font-bold text-primary text-sm">{index + 1}</span>
                                    </div>

                                    {/* Empty Side for Balance */}
                                    <div className="hidden lg:block flex-1" />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Methodology Benefits */}
            <section className="py-20 bg-secondary/30">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold mb-12">Por Que É Tão Rápido?</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-background p-8 rounded-2xl border border-border">
                            <div className="w-16 h-16 mx-auto bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-6 text-blue-600">
                                <Settings className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold mb-4">No-Code Core</h3>
                            <p className="text-muted-foreground">
                                Não escrevemos código para configurar seus processos. Usamos nossa interface visual de arrastar e soltar.
                            </p>
                        </div>
                        <div className="bg-background p-8 rounded-2xl border border-border">
                            <div className="w-16 h-16 mx-auto bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mb-6 text-purple-600">
                                <PlayCircle className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold mb-4">Pré-Configurado</h3>
                            <p className="text-muted-foreground">
                                A plataforma já vem com templates e melhores práticas de ITSM e ESM embutidos.
                            </p>
                        </div>
                        <div className="bg-background p-8 rounded-2xl border border-border">
                            <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-6 text-green-600">
                                <Rocket className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold mb-4">AI-Native</h3>
                            <p className="text-muted-foreground">
                                A VIA aprende com seus dados históricos, acelerando a configuração da base de conhecimento.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-primary/5 border-t border-primary/10">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                        Sua Transformação Começa Agora
                    </h2>
                    <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                        Agende uma conversa com nossos especialistas e receba um plano de implementação personalizado.
                    </p>
                    <Button size="lg" className="group" onClick={() => setDemoOpen(true)}>
                        Agendar Diagnóstico Gratuito
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>
            </section>

            <Footer />
            <DemoScheduler open={demoOpen} onOpenChange={setDemoOpen} />
        </div>
    );
};

export default HowItWorksPage;
